import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { type IEBInboxTask } from '@shared/models/entities/inbox-task.interface';
import { getPriorityLabel } from '@shared/models/priority.enum';
import { resolveTaskStatus, TaskStatus } from '@shared/models/task-status.enum';
import { getTaskTypeIcon, getTaskTypeLabel } from '@shared/utilities/task-type.utils';
import { UtilityDockStore } from '../utility-dock.store';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { TranslateService } from '@core/services/translate.service';

@Component({
  selector: 'app-inbox-item',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzIconModule,
    NzToolTipModule,
    TranslatePipe,
  ],
  templateUrl: './inbox-item.component.html',
  styleUrls: ['./inbox-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InboxItemComponent {
  @Input() item!: IEBInboxTask;
  @Output() navigate = new EventEmitter<string>();
  @Output() markDone = new EventEmitter<string>();
  @Output() assignToMe = new EventEmitter<string>();
  @Output() remove = new EventEmitter<string>();

  private readonly router = inject(Router);
  private readonly utilityDockStore = inject(UtilityDockStore);
  private readonly translate = inject(TranslateService);

  onItemClick(): void {
    // Close drawer first
    this.utilityDockStore.closeInbox();

    if (this.isPrivateTask()) {
      this.router.navigate(['/tasks/private', this.item.inboxTaskId], {
        state: { privateTask: this.item },
      });
      return;
    }
    
    if (this.item.entityId) {
      this.router.navigate(['/tasks/do', this.item.entityId]);
    } else {
      this.navigate.emit(this.item.inboxTaskId);
    }
  }

  onItemKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onItemClick();
    }
  }

  getTaskTypeIcon(): string {
    return getTaskTypeIcon(this.item.entityType);
  }

  displayIcon(): string {
    if (this.isPrivateTask()) {
      return 'lock';
    }

    return this.getTaskTypeIcon();
  }

  getTaskTypeLabel(): string {
    return getTaskTypeLabel(this.item.entityType);
  }

  taskTypeLabel(): string {
    if (this.isPrivateTask()) {
      return this.translate.instant('activity.inbox.private');
    }

    return this.getTaskTypeLabel() || this.translate.instant('inbox.type.task');
  }

  priorityLabel(): string {
    return getPriorityLabel(this.item.priority);
  }

  priorityIntent(): 'info' | 'constructive' | 'warning' | 'destructive' {
    const priority = this.item.priority?.toString().toUpperCase();

    if (priority === 'LOW') return 'constructive';
    if (priority === 'HIGH') return 'warning';
    if (priority === 'CRITICAL') return 'destructive';

    return 'info';
  }

  tileIntent(): 'info' | 'constructive' | 'warning' | 'destructive' | 'neutral' {
    if (this.isPrivateTask()) {
      return 'neutral';
    }

    return this.priorityIntent();
  }

  priorityIcon(): string {
    const intent = this.priorityIntent();

    if (intent === 'constructive') return 'down';
    if (intent === 'warning') return 'up';
    if (intent === 'destructive') return 'warning';

    return 'pause';
  }

  isOverdue(): boolean {
    if (!this.item.dueAt || this.isDone()) {
      return false;
    }

    return new Date(this.item.dueAt).getTime() < Date.now();
  }

  statusIntent(): 'info' | 'constructive' | 'warning' {
    if (this.isDone()) {
      return 'constructive';
    }

    if (this.isOverdue()) {
      return 'warning';
    }

    return 'info';
  }

  statusIcon(): string {
    if (this.isDone()) {
      return 'check-circle';
    }

    if (this.isOverdue()) {
      return 'clock-circle';
    }

    return 'play-circle';
  }

  statusLabel(): string {
    if (this.isDone()) {
      return this.translate.instant('inbox.status.done');
    }

    if (this.isAssignedTask()) {
      return this.translate.instant('inbox.status.assigned');
    }

    return this.translate.instant('inbox.status.open');
  }

  isAssignedTask(): boolean {
    return this.item.entityType === 'ASSIGNED_TASK';
  }

  isDone(): boolean {
    return resolveTaskStatus(this.item.status, TaskStatus.IN_PROGRESS) === TaskStatus.DONE;
  }

  isPrivateTask(): boolean {
    const kind = String(this.item.kind ?? '').trim().toLowerCase();
    if (kind) return kind === 'private';

    const hasNotificationSource = String(this.item.sourceNotificationId ?? '').trim().length > 0;
    const hasEntityContext = String(this.item.entityId ?? '').trim().length > 0;
    return !hasNotificationSource && !hasEntityContext && !this.isAssignedTask();
  }

  mobileMetaParts(): string[] {
    const parts: string[] = [this.taskTypeLabel()];

    if (this.item.priority) {
      parts.push(this.priorityLabel());
    }

    if (this.isAssignedTask()) {
      parts.push(this.translate.instant('inbox.status.assigned'));
    }

    return parts;
  }
}
